/**
 * Single source of truth for the admin sidebar.
 *
 * Before this file the menu was 284 lines of hand-written HTML plus a SECOND
 * hand-written `routeToGroup` map in Dashboard.js — every new page had to be
 * added twice and the two drifted apart (34 registered routes were reachable
 * only by typing the URL). Now the markup, the route→group mapping and the
 * search index are all derived from this one array.
 *
 * Item shape:
 *   path     route to push (must exist in main.js)
 *   label    what the admin reads — it must describe the CONTENT, not the table name
 *   desc     one line shown under the label in search results, so you know what a
 *            page does before opening it (this is what "المالية" pages were missing)
 *   icon     iconify id
 *   keywords extra search terms (Arabic synonyms + the English route name), so
 *            "commission" and "نسبة" and "ارباح" all find قواعد العمولة
 *   roles    OPTIONAL per-item override of the group's roles
 *   badge    OPTIONAL small chip ("جديد")
 *
 * roles = Users.UserType values: 1 super-admin, 40 school owner, 41 school admin.
 */

export const ROLE_ADMIN = 1;
export const ROLE_SCHOOL_OWNER = 40;
export const ROLE_SCHOOL_ADMIN = 41;

const ADMIN = [ROLE_ADMIN];
const SCHOOL = [ROLE_SCHOOL_OWNER, ROLE_SCHOOL_ADMIN];

export const MENU_GROUPS = [
    // ================= الرئيسية =================
    {
        key: 'overview',
        label: 'الرئيسية',
        icon: 'solar:widget-5-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/Home', label: 'نظرة عامة', desc: 'ملخّص أرقام النظام: الرحلات والمستخدمون والأرصدة', icon: 'solar:atom-line-duotone', keywords: 'home رئيسية لوحة overview ملخص' },
            { path: '/Statistic', label: 'الإحصائيات', desc: 'رسوم بيانية لأعداد الرحلات والمستخدمين', icon: 'solar:screencast-2-line-duotone', keywords: 'statistic احصائيات charts بيانات' },
            { path: '/Events', label: 'آخر الأحداث', desc: 'أحدث أحداث النظام + أكثر المدن نشاطًا هذا الشهر', icon: 'solar:chart-line-duotone', keywords: 'events سجل نشاط مدن' }
        ]
    },

    // ================= المتابعة المباشرة =================
    {
        key: 'live',
        label: 'المتابعة المباشرة',
        icon: 'solar:map-point-wave-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/LiveTracking', label: 'التتبع المباشر', desc: 'خريطة حيّة لكل الرحلات الجارية ومسار كل سائق', icon: 'solar:map-point-wave-line-duotone', keywords: 'livetracking تتبع خريطة مباشر gps', badge: 'جديد' },
            { path: '/Cameras', label: 'الكاميرات المباشرة', desc: 'بث مباشر من كاميرات الرحلات النشطة', icon: 'solar:videocamera-record-line-duotone', keywords: 'cameras كاميرا بث فيديو', badge: 'جديد' },
            { path: '/CurrentRequests', label: 'الطلبات المعلّقة', desc: 'طلبات ركّاب بانتظار إسناد سائق — يمكن إسنادها يدويًا', icon: 'solar:clock-circle-line-duotone', keywords: 'currentrequests طلبات معلقة pending انتظار' },
            { path: '/CurrentTrips', label: 'الرحلات الجارية', desc: 'الرحلات قيد التنفيذ الآن مع إمكانية الإلغاء أو تبديل السائق', icon: 'solar:map-arrow-right-line-duotone', keywords: 'currenttrips جارية حالية نشطة' },
            { path: '/SchoolTripsLive', label: 'الرحلات المدرسية الآن', desc: 'لوحة تتحدّث كل 15 ثانية للرحلات المدرسية الجارية', icon: 'solar:map-arrow-square-line-duotone', keywords: 'schooltripslive مدرسية جارية باص' },
            { path: '/Sos', label: 'بلاغات الطوارئ SOS', desc: 'بلاغات الاستغاثة الحيّة والسجل + إنهاء البلاغ', icon: 'solar:siren-rounded-line-duotone', keywords: 'sos طوارئ استغاثة نجدة emergency', danger: true }
        ]
    },

    // ================= الرحلات =================
    {
        key: 'trips',
        label: 'الرحلات',
        icon: 'solar:wheel-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/Trips', label: 'كل الرحلات', desc: 'سجل كل الرحلات مع فلاتر الحالة والتاريخ + إسناد وإلغاء', icon: 'solar:notes-line-duotone', keywords: 'trips رحلات سجل history كل' },
            { path: '/ScheduledTrips', label: 'الرحلات المجدولة', desc: 'رحلات محجوزة لوقت لاحق', icon: 'solar:calendar-mark-line-duotone', keywords: 'scheduledtrips مجدولة حجز لاحق' },
            { path: '/SharedTrips', label: 'الرحلات المشتركة', desc: 'رحلات يتقاسمها أكثر من راكب', icon: 'solar:users-group-two-rounded-line-duotone', keywords: 'sharedtrips مشتركة تشاركية pool' },
            { path: '/SchoolTrips', label: 'المسارات المدرسية', desc: 'إنشاء مسار، ربط الطلاب، ترتيب المحطات، تبديل السائق', icon: 'solar:backpack-line-duotone', keywords: 'schooltrips مدرسة مسار باص طلاب' }
        ]
    },

    // ================= الرحلات التعاقدية =================
    // «الرحلة المخصصة» تجاريًا «رحلة رفيق»: عقد نقل متكرر يولّد رحلات يومية.
    // قسم مستقل لأن دورة حياته (طلب ← عرض سعر ← عقد ← مجموعات ← إسناد ← فوترة)
    // لا تُعبَّر عنها صفحة الرحلات العامة — والرحلات المولّدة تظهر هناك أيضًا بفلتر النوع.
    {
        key: 'contracts',
        label: 'الرحلات التعاقدية',
        icon: 'solar:document-text-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/ContractRequests', label: 'الطلبات وعروض الأسعار', desc: 'مراجعة طلبات التعاقد، حساب السعر المقترح، إصدار العرض واعتماده', icon: 'solar:inbox-line-line-duotone', keywords: 'contractrequests طلبات تعاقد عرض سعر quote عروض', badge: 'جديد' },
            { path: '/Contracts', label: 'العقود', desc: 'العقود النشطة: المجموعات، الجدول، الركاب، الإسناد، التعديلات', icon: 'solar:document-text-line-duotone', keywords: 'contracts عقود تعاقد مخصصة رفيق' },
            { path: '/ContractCompanies', label: 'الشركات والموظفون', desc: 'بيانات الشركات، استيراد الموظفين، الحد الائتماني', icon: 'solar:buildings-2-line-duotone', keywords: 'companies شركات موظفين مؤسسات استيراد' },
            { path: '/ContractPlanning', label: 'خطة 30 يومًا', desc: 'الرحلات المخططة للشهر القادم والتعارضات والأيام بلا سائق', icon: 'solar:calendar-line-duotone', keywords: 'contractplanning خطة تخطيط جدول قادم' },
            { path: '/ContractTripsToday', label: 'متابعة اليوم', desc: 'رحلات العقود اليوم، التصعيدات، وحالة آخر تشغيل للوظائف', icon: 'solar:map-point-wave-line-duotone', keywords: 'contracttripstoday اليوم متابعة مباشر تصعيد' },
            { path: '/ContractInvoices', label: 'فواتير العقود', desc: 'الفواتير والمتأخرات وفترات السماح والتسويات', icon: 'solar:bill-list-line-duotone', keywords: 'contractinvoices فواتير متأخرات ائتمان' }
        ]
    },

    // ================= السائقون والمركبات =================
    {
        key: 'fleet',
        label: 'السائقون والمركبات',
        icon: 'solar:user-id-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/Drivers', label: 'السائقون', desc: 'اعتماد أو رفض السائق والمركبة، الإيقاف، الربط بمدرسة', icon: 'solar:user-id-line-duotone', keywords: 'drivers سائق سواق اعتماد موافقة' },
            { path: '/SuspendedDrivers', label: 'السائقون الموقوفون', desc: 'السائقون المعلَّقون + إلغاء الإيقاف', icon: 'solar:user-cross-line-duotone', keywords: 'suspendeddrivers موقوف معلق حظر سائق' },
            { path: '/DriversRatings', label: 'تقييمات السائقين', desc: 'كل تقييمات الركّاب للسائقين', icon: 'solar:star-line-duotone', keywords: 'driversratings تقييم نجوم rating' },
            { path: '/Vehicles', label: 'المركبات', desc: 'بيانات المركبات وأنواعها وربطها بالسائقين', icon: 'solar:bus-line-duotone', keywords: 'vehicles مركبة سيارة عربة لوحة' },
            { path: '/SuspendedVehicles', label: 'المركبات الموقوفة', desc: 'المركبات المعلَّقة + إلغاء الإيقاف', icon: 'solar:bus-line-duotone', keywords: 'suspendedvehicles مركبة موقوفة معلقة' }
        ]
    },

    // ================= الركاب وأولياء الأمور =================
    {
        key: 'customers',
        label: 'الركاب وأولياء الأمور',
        icon: 'solar:users-group-rounded-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/Passengers', label: 'الركاب', desc: 'حسابات الركّاب ومواقعهم المحفوظة + الإيقاف', icon: 'solar:user-line-duotone', keywords: 'passengers راكب ركاب زبون عميل' },
            { path: '/SuspendedPassengers', label: 'الركاب الموقوفون', desc: 'الركّاب المعلَّقون + إلغاء الإيقاف', icon: 'solar:user-cross-line-duotone', keywords: 'suspendedpassengers راكب موقوف معلق' },
            { path: '/Parents', label: 'أولياء الأمور', desc: 'حسابات أولياء الأمور وربط الأبناء', icon: 'solar:users-group-rounded-line-duotone', keywords: 'parents ولي امر اب ام والد' },
            { path: '/Students', label: 'الطلاب', desc: 'بيانات الطلاب وربطهم بالمدارس', icon: 'solar:square-academic-cap-line-duotone', keywords: 'students طالب تلميذ مدرسة' }
        ]
    },

    // ================= المدارس =================
    {
        key: 'schools',
        label: 'المدارس',
        icon: 'solar:book-2-line-duotone',
        roles: [ROLE_ADMIN, ROLE_SCHOOL_OWNER, ROLE_SCHOOL_ADMIN],
        items: [
            { path: '/Schools', label: 'قائمة المدارس', desc: 'بيانات المدارس ومحفظة كل مدرسة واشتراكاتها', icon: 'solar:buildings-3-line-duotone', keywords: 'schools مدرسة مدارس', roles: ADMIN },
            { path: '/SchoolsUsers', label: 'مسؤولو المدارس', desc: 'حسابات موظفي المدارس', icon: 'solar:user-id-line-duotone', keywords: 'schoolsusers مسؤول مدير مدرسة موظف', roles: [ROLE_ADMIN, ROLE_SCHOOL_OWNER] },
            { path: '/Students', label: 'الطلاب', desc: 'بيانات طلاب مدرستك', icon: 'solar:square-academic-cap-line-duotone', keywords: 'students طالب تلميذ', roles: SCHOOL },
            { path: '/Parents', label: 'أولياء الأمور', desc: 'أولياء أمور طلاب مدرستك', icon: 'solar:users-group-rounded-line-duotone', keywords: 'parents ولي امر', roles: SCHOOL },
            { path: '/SchoolTrips', label: 'المسارات المدرسية', desc: 'مسارات الباص وربط الطلاب بالمحطات', icon: 'solar:backpack-line-duotone', keywords: 'schooltrips مسار باص', roles: SCHOOL },
            { path: '/SchoolTripsLive', label: 'الرحلات الجارية الآن', desc: 'لوحة حيّة للرحلات المدرسية قيد التنفيذ', icon: 'solar:map-arrow-square-line-duotone', keywords: 'schooltripslive جارية مباشر', roles: SCHOOL },
            { path: '/SchoolAttendanceReport', label: 'تقرير الحضور والغياب', desc: 'غياب الطلاب لكل مدرسة خلال فترة', icon: 'solar:calendar-mark-line-duotone', keywords: 'schoolattendancereport حضور غياب تقرير' },
            { path: '/SchoolInvoices', label: 'فواتير المدارس', desc: 'فواتير كل مدرسة خلال فترة + إعادة توليد الفاتورة', icon: 'solar:bill-list-line-duotone', keywords: 'schoolinvoices فاتورة فواتير محاسبة', roles: ADMIN },
            { path: '/Drivers', label: 'السائقون', desc: 'سائقو مدرستك', icon: 'solar:user-id-line-duotone', keywords: 'drivers سائق', roles: SCHOOL },
            { path: '/Vehicles', label: 'المركبات', desc: 'مركبات مدرستك', icon: 'solar:bus-line-duotone', keywords: 'vehicles مركبة باص', roles: SCHOOL },
            { path: '/AdminNotifications', label: 'إشعار لأولياء الأمور', desc: 'إرسال إشعار جماعي لأولياء أمور مدرستك', icon: 'solar:plain-2-line-duotone', keywords: 'notification اشعار رسالة تنبيه', roles: SCHOOL }
        ]
    },

    // ================= المال والمحافظ =================
    // The old "المالية والاشتراكات" group was 10 pages whose names were
    // indistinguishable. Labels now say WHICH DIRECTION the money moves.
    {
        key: 'money',
        label: 'المال والمحافظ',
        icon: 'solar:wallet-money-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/RevenueDashboard', label: 'الإيرادات والأرباح', desc: 'إيراد اليوم/الشهر وعمولة رفيق مع رسوم بيانية', icon: 'solar:chart-2-line-duotone', keywords: 'revenue sales مبيعات ايراد ارباح دخل عمولة مكسب' },
            { path: '/WalletsDashboard', label: 'أرصدة المحافظ', desc: 'الرصيد الحالي لكل مستخدم — لقطة، لا حركات', icon: 'solar:wallet-2-line-duotone', keywords: 'walletsdashboard رصيد محفظة balance ارصدة' },
            { path: '/FinancialTracker', label: 'حركات المحافظ (قبل/بعد)', desc: 'كل حركة على المحفظة مع الرصيد قبلها وبعدها', icon: 'solar:money-bag-line-duotone', keywords: 'financialtracker حركة كشف سجل محفظة قبل بعد' },
            { path: '/FinancialRecharge', label: 'شحن المحافظ (وارد)', desc: 'الأموال الداخلة إلى المحافظ وطريقة الدفع', icon: 'solar:card-recive-line-duotone', keywords: 'financialrecharge شحن تعبئة ايداع داخل وارد محفظة محافظ topup' },
            { path: '/WalletPurchases', label: 'مشتريات المحافظ (صادر)', desc: 'الأموال الخارجة من المحافظ — الاشتراكات وغيرها', icon: 'solar:bag-3-line-duotone', keywords: 'walletpurchases مشتريات شراء خارج صادر صرف محفظة محافظ' },
            { path: '/Financial', label: 'طلبات السحب والقيود اليدوية', desc: 'اعتماد أو رفض سحب أرباح السائق + إضافة قيد مالي يدوي', icon: 'solar:document-text-line-duotone', keywords: 'financial withdrawing سحب اعتماد رفض قيد يدوي مالية', aliases: ['/FinancialWithdrawing'] },
            { path: '/SubscriptionsDashboard', label: 'الاشتراكات — نظرة عامة', desc: 'عدد الاشتراكات النشطة والإيراد الشهري والمنتهية قريبًا', icon: 'solar:medal-ribbon-line-duotone', keywords: 'subscriptionsdashboard اشتراك باقة نشط mrr' },
            { path: '/FinancialSubscriptions', label: 'سجل حركات الاشتراكات', desc: 'كل عملية اشتراك: المشترك والخطة والمدة والقيمة', icon: 'solar:letter-line-duotone', keywords: 'financialsubscriptions اشتراك سجل حركة تجديد' }
        ]
    },

    // ================= كروت الشحن =================
    {
        key: 'cards',
        label: 'كروت الشحن',
        icon: 'solar:card-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/VoucherCardsDashboard', label: 'نظرة عامة على الكروت', desc: 'عدد الباقات والكروت والمشحون والمتبقي', icon: 'solar:pie-chart-2-line-duotone', keywords: 'vouchercardsdashboard كروت نظرة ملخص' },
            { path: '/VoucherCards', label: 'باقات الكروت', desc: 'توليد دفعة كروت، بيعها لموزّع، تجميدها أو حذفها', icon: 'solar:ticket-line-duotone', keywords: 'vouchercards باقة دفعة توليد بيع تجميد' },
            { path: '/Cards', label: 'قائمة الكروت', desc: 'كل كرت على حدة: الرقم التسلسلي والقيمة والحالة', icon: 'solar:card-line-duotone', keywords: 'cards كرت كروت قائمة تسلسلي' },
            { path: '/VoucherCardsCards', label: 'بحث عن كرت', desc: 'استعلام عن كرت برقمه التسلسلي أو رقم القسيمة', icon: 'solar:magnifer-line-duotone', keywords: 'vouchercardscards بحث استعلام كرت قسيمة serial' },
            { path: '/Charging', label: 'عمليات الشحن الناجحة', desc: 'سجل الكروت التي استُبدلت فعلًا ومن استبدلها', icon: 'solar:battery-charge-line-duotone', keywords: 'charging شحن ناجح استبدال redeem' },
            { path: '/VoucherCardsTryAttemp', label: 'محاولات الشحن الفاشلة', desc: 'محاولات استبدال مرفوضة وسببها — مؤشر تلاعب', icon: 'solar:shield-warning-line-duotone', keywords: 'vouchercardstryattemp فاشل محاولة خطأ رفض' },
            { path: '/VoucherCardsDistributors', label: 'الموزعون', desc: 'موزّعو الكروت وما بيع ومتبقٍ لكل موزّع', icon: 'solar:shop-line-duotone', keywords: 'distributors موزع وكيل بائع' }
        ]
    },

    // ================= التسعير والعمولات =================
    // Pulled out of the 13-item "إعدادات النظام" bucket: these are the dials
    // that decide how much the rider pays and how much Rafiq keeps.
    {
        key: 'pricing',
        label: 'التسعير والعمولات',
        icon: 'solar:tag-price-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/Pricing', label: 'التسعير الديناميكي', desc: 'سعر الفتح والكيلومتر والدقيقة ومضاعفات الذروة والليل', icon: 'solar:tag-price-line-duotone', keywords: 'pricing تسعير سعر اجرة تعريفة كيلومتر ذروة surge' },
            { path: '/CommissionRules', label: 'قواعد العمولة (نسبة رفيق)', desc: 'النسبة التي يأخذها رفيق من كل رحلة — حسب الفئة ونوع السائق', icon: 'solar:percent-circle-line-duotone', keywords: 'commissionrules عمولة نسبة مئوية ارباح مكسب حصة رفيق percent commission' },
            { path: '/CancellationPolicy', label: 'سياسة الإلغاء', desc: 'رسوم الإلغاء وعدم الحضور حسب توقيت الإلغاء', icon: 'solar:close-circle-line-duotone', keywords: 'cancellationpolicy الغاء رسوم غرامة عقوبة' },
            { path: '/TripCategories', label: 'فئات الرحلات', desc: 'العادية والفاخرة والنسائية… وأيقونة ومعامل سعر كل فئة', icon: 'solar:layers-minimalistic-line-duotone', keywords: 'tripcategories فئة نوع رحلة عادية فاخرة نسائية' },
            { path: '/SubscriptionsTypes', label: 'باقات اشتراك السائقين', desc: 'خطط الاشتراك: السعر والمدة وعدد الرحلات المشمولة', icon: 'solar:medal-ribbon-line-duotone', keywords: 'subscriptionstypes باقة خطة اشتراك سعر مدة' },
            { path: '/PaymentMethods', label: 'طرق الدفع', desc: 'طرق الدفع المتاحة (نقدي / محفظة / …)', icon: 'solar:card-2-line-duotone', keywords: 'paymentmethods دفع نقد محفظة طريقة' },
            { path: '/PricingChangeLog', label: 'سجل تغييرات الأسعار', desc: 'من غيّر أي سعر ومتى', icon: 'solar:history-line-duotone', keywords: 'pricingchangelog سجل تغيير تدقيق audit' }
        ]
    },

    // ================= بيانات أساسية =================
    {
        key: 'data',
        label: 'بيانات أساسية',
        icon: 'solar:database-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/VehicleTypes', label: 'أنواع المركبات', desc: 'قاموس أنواع المركبات', icon: 'solar:bus-line-duotone', keywords: 'vehicletypes نوع مركبة صنف' },
            { path: '/Cities', label: 'المدن', desc: 'قاموس المدن', icon: 'solar:city-line-duotone', keywords: 'cities مدينة مدن' },
            { path: '/Municipalities', label: 'البلديات', desc: 'بلديات كل مدينة', icon: 'solar:map-line-duotone', keywords: 'municipalities بلدية' },
            { path: '/Locations', label: 'المواقع', desc: 'مواقع/مناطق كل بلدية', icon: 'solar:map-point-line-duotone', keywords: 'locations موقع منطقة حي' },
            { path: '/Faq', label: 'الأسئلة الشائعة', desc: 'الأسئلة التي تظهر في التطبيق', icon: 'solar:question-circle-line-duotone', keywords: 'faq اسئلة شائعة مساعدة' },
            { path: '/SmsSimulator', label: 'محاكي الرسائل (SMS)', desc: 'نصوص رسائل OTP التي كان النظام سيرسلها — للاختبار', icon: 'solar:chat-square-line-duotone', keywords: 'smssimulator رسالة sms otp محاكي اختبار' },
            { path: '/Settings', label: 'الإعدادات العامة', desc: 'إعدادات عامة للنظام', icon: 'solar:settings-minimalistic-line-duotone', keywords: 'settings اعدادات ضبط' }
        ]
    },

    // ================= المستخدمون والإشعارات =================
    {
        key: 'people',
        label: 'المستخدمون والإشعارات',
        icon: 'solar:users-group-two-rounded-line-duotone',
        roles: ADMIN,
        items: [
            { path: '/Users', label: 'المستخدمون والصلاحيات', desc: 'حسابات النظام: إضافة، تعديل كلمة المرور، الحالة', icon: 'solar:user-line-duotone', keywords: 'users مستخدم حساب صلاحية ادمن' },
            { path: '/UsersSuspend', label: 'الحسابات الموقوفة', desc: 'الحسابات المعلَّقة + إلغاء الإيقاف', icon: 'solar:user-cross-line-duotone', keywords: 'userssuspend موقوف معلق حظر' },
            { path: '/UsersProfile', label: 'ملفي الشخصي', desc: 'تعديل بياناتك وكلمة مرورك وصورتك', icon: 'solar:user-circle-line-duotone', keywords: 'usersprofile ملف شخصي كلمة مرور صورة' },
            { path: '/UsersNotifications', label: 'صندوق الإشعارات', desc: 'الإشعارات الواردة لك + تعليمها كمقروءة', icon: 'solar:bell-line-duotone', keywords: 'usersnotifications صندوق وارد اشعار' },
            { path: '/AdminNotifications', label: 'إرسال إشعار عام', desc: 'بثّ إشعار لكل المستخدمين أو لدور معيّن', icon: 'solar:bell-bing-line-duotone', keywords: 'adminnotifications بث اشعار جماعي broadcast تنبيه' },
            { path: '/SendNotificationByTarget', label: 'إشعار موجّه', desc: 'إرسال إشعار لفئة أو مدرسة محددة', icon: 'solar:plain-2-line-duotone', keywords: 'sendnotificationbytarget موجه استهداف اشعار' },
            { path: '/Reports', label: 'سجل البلاغات والشكاوى', desc: 'شكاوى المستخدمين: عرض، رد، إغلاق', icon: 'solar:document-add-line-duotone', keywords: 'reports بلاغ شكوى تذكرة رد' },
            { path: '/TrustedContacts', label: 'جهات الاتصال الموثوقة', desc: 'جهات الطوارئ المسجَّلة لمستخدم معيّن', icon: 'solar:users-group-rounded-line-duotone', keywords: 'trustedcontacts جهة اتصال موثوق طوارئ' }
        ]
    }
];

/** Arabic-tolerant normalizer: أ/إ/آ→ا, ة→ه, ى→ي, ؤ/ئ→و/ي, strips tashkeel & tatweel. */
export function normalizeAr(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[ً-ْـ]/g, '')
        .replace(/[أإآٱ]/g, 'ا')
        .replace(/ة/g, 'ه')
        .replace(/[ىي]/g, 'ي')
        .replace(/ؤ/g, 'و')
        .replace(/ئ/g, 'ي')
        .replace(/\s+/g, ' ')
        .trim();
}

/** True when a userType may see this group/item. */
export function isAllowed(entry, groupRoles, userType) {
    const roles = entry.roles || groupRoles;
    return Array.isArray(roles) && roles.indexOf(Number(userType)) !== -1;
}

/** Groups (and items) the given userType may see. */
export function visibleGroups(userType) {
    const type = Number(userType);
    return MENU_GROUPS
        .map(group => {
            const items = group.items.filter(item => isAllowed(item, group.roles, type));
            return items.length ? Object.assign({}, group, { items }) : null;
        })
        .filter(Boolean);
}

/**
 * Flat, pre-normalized search index. The fields are kept SEPARATE (not one
 * concatenated blob) so a hit on the page title can outrank a hit on a
 * keyword — otherwise searching "عمولة" surfaced الإيرادات (which merely lists
 * عمولة as a synonym) above قواعد العمولة itself.
 */
export function searchIndex(userType) {
    const index = [];
    visibleGroups(userType).forEach(group => {
        group.items.forEach(item => {
            index.push({
                item,
                groupKey: group.key,
                groupLabel: group.label,
                nLabel: normalizeAr(item.label),
                nDesc: normalizeAr(item.desc),
                nExtra: normalizeAr([item.keywords, item.path, group.label].join(' '))
            });
        });
    });
    return index;
}

/**
 * Score one indexed row against already-normalized terms.
 * Returns 0 when any term is missing (all terms must match somewhere).
 */
export function scoreRow(row, terms) {
    let total = 0;
    for (let i = 0; i < terms.length; i++) {
        const term = terms[i];
        let score = 0;
        // Fields are SUMMED, not "best wins": a page that carries the term in its
        // title AND its keywords is more relevant than one that merely mentions it
        // in a sentence. (Arabic broken plurals — محفظة vs المحافظ — mean the title
        // often misses, so the extra signal decides an otherwise arbitrary tie.)
        if (row.nLabel.indexOf(term) === 0) score += 10;         // title starts with it
        else if (row.nLabel.indexOf(term) !== -1) score += 7;    // title contains it
        if (row.nDesc.indexOf(term) !== -1) score += 3;          // description
        if (row.nExtra.indexOf(term) !== -1) score += 2;         // keyword / route / group
        if (!score) return 0;                                    // every term must hit
        total += score;
    }
    return total;
}

/** Route slug, lowercased and stripped of the leading slash + any sub-path. */
function slugOf(path) {
    return normalizeAr(String(path || '').replace(/^\//, '').split('/')[0].split('?')[0]);
}

/**
 * True when `path` is this item's own route or one of its aliases — a page
 * reachable under a second URL (e.g. /FinancialWithdrawing → the Financial hub)
 * must still light up its menu entry.
 */
export function itemMatchesPath(item, path) {
    const slug = slugOf(path);
    if (!slug) return false;
    if (slugOf(item.path) === slug) return true;
    return (item.aliases || []).some(alias => slugOf(alias) === slug);
}

/**
 * Which group holds `path`, for the given user. Replaces the hand-written
 * routeToGroup map that had to be updated separately from the markup.
 */
export function groupKeyForPath(path, userType) {
    if (!slugOf(path)) return null;
    const groups = visibleGroups(userType);
    for (let g = 0; g < groups.length; g++) {
        for (let i = 0; i < groups[g].items.length; i++) {
            if (itemMatchesPath(groups[g].items[i], path)) return groups[g].key;
        }
    }
    return null;
}
